import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  FaChartLine,
  FaSyncAlt,
  FaDollarSign,
  FaUsers,
  FaTicketAlt,
  FaCheckCircle,
} from "react-icons/fa";

// ---------------------- Utilities ----------------------
const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

const num = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const pct = (n) => `${(n ?? 0).toFixed(2)}%`;

const todayYMD = () => new Date().toISOString().slice(0, 10);
const daysAgoYMD = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

// ---------------------- Data Hook ----------------------
function useDashboard(apiBase) {
  const [from, setFrom] = useState(daysAgoYMD(7));
  const [to, setTo] = useState(todayYMD());
  // const [scope, setScope] = useState("ALL");
  const scope = "ALL";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [error, setError] = useState(null);

  const qs = `from=${from}&to=${to}&scope=${scope}`;

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiBase}?${qs}`);
      setData(res.data || {});
    } catch (err) {
      // Dashboard fetch failed
      setError(err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchData, 60000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  return {
    from,
    to,
    scope,
    setFrom,
    setTo,
    // setScope,
    data,
    loading,
    error,
    refresh: fetchData,
    autoRefresh,
    setAutoRefresh,
  };
}

// ---------------------- UI Components ----------------------
const KPITile = ({ label, value, sub, icon: Icon, trend, gradient }) => (
  <div
    className="relative overflow-hidden rounded-xl p-5 shadow-lg border border-gray-100"
    style={{
      background:
        gradient || "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: 500,
            color: "#6b7280",
            marginBottom: "8px",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: "28px",
            fontWeight: 700,
            color: "#111827",
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </div>
        {sub && (
          <div
            style={{ marginTop: "8px", fontSize: "12px", color: "#9ca3af" }}
          >
            {sub}
          </div>
        )}
        {trend && (
          <div
            style={{
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
              fontWeight: 500,
              color: "#10b981",
            }}
          >
            <FaChartLine style={{ fontSize: "12px" }} />
            <span>{trend}</span>
          </div>
        )}
      </div>
      {Icon && (
        <div
          style={{
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.6)",
            padding: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon style={{ fontSize: "24px", color: "#4b5563" }} />
        </div>
      )}
    </div>
  </div>
);

const Section = ({ title, children, icon: Icon }) => (
  <div
    className="rounded-xl p-6 shadow-lg border border-gray-100"
    style={{ background: "#ffffff" }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "24px",
      }}
    >
      {Icon && (
        <div
          style={{
            borderRadius: "8px",
            background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)",
            padding: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon style={{ fontSize: "18px", color: "#2563eb" }} />
        </div>
      )}
      <h2
        style={{
          fontSize: "20px",
          fontWeight: 700,
          color: "#111827",
          margin: 0,
        }}
      >
        {title}
      </h2>
    </div>
    {children}
  </div>
);

// Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "#ffffff",
        padding: "16px",
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <p
        style={{
          fontWeight: 600,
          color: "#111827",
          marginBottom: "8px",
          fontSize: "14px",
        }}
      >
        {label}
      </p>
      {payload.map((entry, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "13px",
            marginBottom: "4px",
          }}
        >
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              background: entry.color,
            }}
          />
          <span style={{ color: "#6b7280" }}>{entry.name}:</span>
          <span style={{ fontWeight: 600, color: "#111827" }}>
            {entry.name.includes("Revenue")
              ? inr(entry.value)
              : num(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ---------------------- Main Page ----------------------
export function DashboardPage() {
  const apiBase = (process.env.REACT_APP_API_URL || "https://api.jaisalmertrust.com") + "/admin/dashboard";

  const {
    from,
    to,
    scope,
    setFrom,
    setTo,
    // setScope,
    data,
    loading,
    error,
    refresh,
    autoRefresh,
    setAutoRefresh,
  } = useDashboard(apiBase);

  const kpi = data?.kpis || {};
  const funnel = data?.funnel || {};
  const range = data?.range;
  const updatedAt = data?.updatedAt;
  const ok = data?.ok;

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "0px 32px",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <header
          className="rounded-xl p-6 shadow-lg border border-gray-100"
          style={{ background: "#ffffff", marginBottom: "24px" }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <h1
                style={{
                  fontSize: "32px",
                  fontWeight: 700,
                  background: "linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  margin: 0,
                  marginBottom: "8px",
                }}
              >
                Jaisalmer Trust Dashboard
              </h1>
              <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
                Range: {range?.from || from} → {range?.to || to}{" "}
                {range?.tz ? `(${range.tz})` : ""}
              </p>
              <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
                Scope: <strong>{data?.scope || scope}</strong> · Status:{" "}
                <strong style={{ color: loading ? "#d97706" : ok ? "#16a34a" : "#dc2626" }}>
                  {loading ? "Pending..." : ok ? "OK" : "Error"}
                </strong>{" "}
                {updatedAt && (
                  <>
                    · Last updated:{" "}
                    {new Date(updatedAt).toLocaleString("en-IN", {
                      timeZone: range?.tz || "Asia/Kolkata",
                    })}
                  </>
                )}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "flex-end",
              }}
            >
              {/* Date filters */}
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  From
                </label>
                <input
                  type="date"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  To
                </label>
                <input
                  type="date"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>

              {/* Scope drop down removed */}

              <button
                onClick={refresh}
                disabled={loading}
                style={{
                  padding: "10px 16px",
                  borderRadius: "8px",
                  background: loading
                    ? "#9ca3af"
                    : "linear-gradient(90deg, #000000 0%, #1f1f1f 100%)",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  border: "none",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaSyncAlt className={loading ? "fa-spin" : ""} />
                {loading ? "Refreshing…" : "Refresh"}
              </button>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "black",
                  }}
                />
                Auto refresh
              </label>
            </div>
          </div>
        </header>

        {error && (
          <div
            style={{
              background: "#fee2e2",
              color: "#991b1b",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "24px",
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* KPI Row */}
        {data && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "20px",
                marginBottom: "24px",
              }}
            >
              <KPITile
                label="Net Revenue"
                value={inr(kpi.revenue?.netPaid)}
                sub={
                  <>
                    Gross: {inr(kpi.revenue?.grossPaid)} · Commission:{" "}
                    {inr(kpi.revenue?.commissionPaid)}
                  </>
                }
                icon={FaDollarSign}
                gradient="linear-gradient(135deg, #ffffff 0%, #ffffff 100%)"
              />
              <KPITile
                label="Total Bookings"
                value={num(kpi.bookings?.total)}
                sub={
                  <>
                    Paid: {num(kpi.bookings?.paid)} · Pending:{" "}
                    {num(kpi.bookings?.pending)} · Failed:{" "}
                    {num(kpi.bookings?.failed)} · Refunded:{" "}
                    {num(kpi.bookings?.refunded)}
                  </>
                }
                icon={FaUsers}
                gradient="linear-gradient(135deg, #ffffff 0%, #ffffff 100%)"
              />
              <KPITile
                label="Tickets Issued"
                value={num(kpi.tickets?.issued)}
                sub={
                  <>
                    Used: {num(kpi.tickets?.byStatus?.USED)} · Active:{" "}
                    {num(kpi.tickets?.byStatus?.ACTIVE)} · Expired:{" "}
                    {num(kpi.tickets?.byStatus?.EXPIRED)}
                  </>
                }
                icon={FaTicketAlt}
                gradient="linear-gradient(135deg, #ffffff 0%, #ffffff 100%)"
              />
              <KPITile
                label="Payment Status"
                value={num(kpi.payment?.overall?.success)}
                sub={
                  <>
                    Total: {num(kpi.payment?.overall?.total)} · Pending:{" "}
                    {num(kpi.payment?.overall?.pending)} · Failed:{" "}
                    {num(kpi.payment?.overall?.failed)}
                  </>
                }
                icon={FaCheckCircle}
                gradient="linear-gradient(135deg, #ffffff 0%, #ffffff 100%)"
              />
            </div>

            {/* Funnel + compact breakdowns */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "20px",
                marginBottom: "24px",
              }}
            >
              <Section title="Booking Funnel" icon={FaChartLine}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0,1fr))",
                    gap: "12px",
                  }}
                >
                  {[
                    { label: "Bookings", value: funnel.bookings },
                    { label: "Paid Bookings", value: funnel.paidBookings },
                    { label: "Txns Success", value: funnel.txnsSuccess },
                    { label: "Tickets Issued", value: funnel.ticketsIssued },
                  ].map((f) => (
                    <div
                      key={f.label}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "10px",
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {f.label}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        {num(f.value)}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Bookings Breakdown">
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                    gap: "12px",
                    fontSize: "13px",
                  }}
                >
                  <li>
                    <strong>Total:</strong> {num(kpi.bookings?.total)}
                  </li>
                  <li>
                    <strong>Paid:</strong> {num(kpi.bookings?.paid)}
                  </li>
                  <li>
                    <strong>Pending:</strong> {num(kpi.bookings?.pending)}
                  </li>
                  <li>
                    <strong>Failed:</strong> {num(kpi.bookings?.failed)}
                  </li>
                  <li>
                    <strong>Refunded:</strong> {num(kpi.bookings?.refunded)}
                  </li>
                </ul>
              </Section>

              <Section title="Ticket Status">
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                    gap: "12px",
                    fontSize: "13px",
                  }}
                >
                  <li>
                    <strong>Issued:</strong> {num(kpi.tickets?.issued)}
                  </li>
                  <li>
                    <strong>Active:</strong> {num(kpi.tickets?.byStatus?.ACTIVE)}
                  </li>
                  <li>
                    <strong>Used:</strong> {num(kpi.tickets?.byStatus?.USED)}
                  </li>
                  <li>
                    <strong>Expired:</strong> {num(kpi.tickets?.byStatus?.EXPIRED)}
                  </li>
                </ul>
              </Section>
            </div>
          </>
        )}

        {/* Chart */}
        {data?.timeseries?.byDay?.length > 0 && (
          <Section title="Bookings, Tickets & Revenue Trends" icon={FaChartLine}>
            <div style={{ height: "400px", width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={data.timeseries.byDay}
                  margin={{ left: 10, right: 20, top: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" />
                  <Bar
                    yAxisId="left"
                    dataKey="bookings"
                    name="Bookings"
                    fill="#98A1BC"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="paidBookings"
                    name="Paid Bookings"
                    fill="#c4b5fd"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenueNet"
                    name="Revenue (₹)"
                    stroke="#10b981"
                    strokeWidth={3}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Section>
        )}

        {/* Top Properties */}
        <div style={{ marginTop: "24px", marginBottom: "24px" }}>
          <Section title="Top Properties">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: "14px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                    {["Property", "Revenue", "Bookings"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data?.topProperties || []).map((p) => (
                    <tr
                      key={p.propertyId}
                      style={{ borderBottom: "1px solid #f3f4f6" }}
                    >
                      <td style={{ padding: "16px", fontWeight: 600 }}>
                        {p.title}
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          fontWeight: 600,
                          color: "#10b981",
                        }}
                      >
                        {inr(p.revenue)}
                      </td>
                      <td style={{ padding: "16px" }}>{num(p.bookings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>

        {/* Counter Leaderboard & Agents */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
            gap: "24px",
            marginBottom: "24px",
          }}
        >
          {/* Counter Leaderboard */}
          <Section title="Counter Leaderboard">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: "14px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                    {["Name", "Revenue", "Bookings"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data?.counterLeaderboard || []).map((c) => (
                    <tr
                      key={c.staffId}
                      style={{ borderBottom: "1px solid #f3f4f6" }}
                    >
                      <td style={{ padding: "16px", fontWeight: 600 }}>
                        {c.name}
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          fontWeight: 600,
                          color: "#10b981",
                        }}
                      >
                        {inr(c.revenue)}
                      </td>
                      <td style={{ padding: "16px" }}>{num(c.bookings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Top Agents */}
          <Section title="Top Agents">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: "14px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                    {["Phone", "Bookings"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data?.agents || []).map((a) => (
                    <tr key={a.phone} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "16px", fontWeight: 600 }}>
                        {a.phone}
                      </td>
                      <td style={{ padding: "16px" }}>{num(a.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>



      </div>
    </div>
  );
}
